import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getShareLink } from '../../api/notifications';
import type { ShareLinkResponse } from '../../types/notification';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useLanguage } from '../../i18n/useLanguage';
import './ShareModal.css';

interface ShareModalProps {
  postId: string;
  postTitle?: string;
  onClose: () => void;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  postId,
  onClose,
}) => {
  const { t } = useLanguage();
  const [shareData, setShareData] = useState<ShareLinkResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchShareLink = async () => {
      try {
        setIsLoading(true);
        const data = await getShareLink(postId);
        setShareData(data);
      } catch (err) {
        console.error('Error fetching share link:', err);
        setError(t('share.error'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchShareLink();
  }, [postId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleCopyLink = async () => {
    if (!shareData?.shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareData.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      inputRef.current?.select();
    } catch {
      inputRef.current?.select();
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return createPortal(
    <div className="share-modal-overlay">
      <div ref={modalRef} className="share-modal" role="dialog" aria-modal="true">
        <div className="share-modal-header">
          <h3>{t('share.title')}</h3>
          <button className="share-modal-close" onClick={onClose} aria-label={t('common.cancel')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="share-modal-content">
          {isLoading ? (
            <div className="share-modal-loading">
              <LoadingSpinner size="medium" />
              <span>{t('share.generating')}</span>
            </div>
          ) : error ? (
            <div className="share-modal-error">
              <span>❌</span>
              <p>{error}</p>
            </div>
          ) : (
            <div className="share-copy-section">
              <label htmlFor="share-link">{t('share.linkLabel')}</label>
              <div className="share-link-input-group">
                <input
                  ref={inputRef}
                  id="share-link"
                  type="text"
                  value={shareData?.shareUrl || ''}
                  readOnly
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button className="copy-btn" onClick={handleCopyLink}>
                  {copied ? (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {t('share.copied')}
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      {t('share.copy')}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ShareModal;
